import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Avatar
} from '@mui/material';
import {
  Check,
  Close,
  Flag,
  Reply,
  MoreVert,
  Visibility
} from '@mui/icons-material';
import { format } from 'date-fns';
import reviewService, { Review } from '../../services/reviewService';
import StarRating from '../../components/reviews/StarRating';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`review-tabpanel-${index}`}
      aria-labelledby={`review-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ReviewModerationPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  
  // Dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      if (tabValue === 0) {
        // Pending reviews
        response = await reviewService.getReviewsForModeration(page, rowsPerPage);
      } else {
        // Flagged reviews
        response = await reviewService.getFlaggedReviews(page, rowsPerPage);
      }
      
      // Transform backend data to frontend format
      const transformedReviews = (response.reviews || []).map((review: any) => ({
        id: review.id,
        rating: review.rating,
        title: review.title,
        comment: review.comment,
        productTitle: review.product?.title || 'Unknown Product',
        userName: review.user?.name || 'Unknown User',
        userId: review.user?.id,
        verified: false, // Backend doesn't provide this field yet
        status: review.approved ? 'APPROVED' : 'PENDING',
        helpfulCount: review.helpful_count || 0,
        unhelpfulCount: 0, // Backend doesn't provide this field yet
        adminResponse: review.admin_response,
        adminResponseDate: review.admin_response_date,
        createdAt: review.created_at,
        updatedAt: review.updated_at,
        editable: false
      }));
      
      setReviews(transformedReviews);
      setTotalElements(response.pagination?.total || 0);
    } catch (err) {
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [tabValue, page, rowsPerPage]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleApprove = async (reviewId: number) => {
    try {
      await reviewService.approveReview(reviewId);
      setSuccess('Review approved successfully');
      loadReviews();
    } catch (err) {
      setError('Failed to approve review');
    }
  };

  const handleReject = async () => {
    if (!selectedReview || !rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      await reviewService.rejectReview(selectedReview.id, rejectReason);
      setSuccess('Review rejected successfully');
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedReview(null);
      loadReviews();
    } catch (err) {
      setError('Failed to reject review');
    }
  };

  const handleFlag = async (reviewId: number) => {
    try {
      await reviewService.flagReview(reviewId);
      setSuccess('Review flagged successfully');
      loadReviews();
    } catch (err) {
      setError('Failed to flag review');
    }
  };

  const handleAddResponse = async () => {
    if (!selectedReview || !adminResponse.trim()) {
      setError('Please provide a response');
      return;
    }

    try {
      await reviewService.addAdminResponse(selectedReview.id, adminResponse);
      setSuccess('Response added successfully');
      setResponseDialogOpen(false);
      setAdminResponse('');
      setSelectedReview(null);
      loadReviews();
    } catch (err) {
      setError('Failed to add response');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, review: Review) => {
    setAnchorEl(event.currentTarget);
    setSelectedReview(review);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'FLAGGED':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderReviewTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Product</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Rating</TableCell>
            <TableCell>Review</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reviews.map((review) => (
            <TableRow key={review.id}>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                  {review.productTitle}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.875rem' }}>
                    {review.userName?.charAt(0) || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">{review.userName}</Typography>
                    {review.verified && (
                      <Chip label="Verified" size="small" color="success" />
                    )}
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <StarRating rating={review.rating} size="small" />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {review.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    maxWidth: 300
                  }}
                >
                  {review.comment}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {review.createdAt ? format(new Date(review.createdAt), 'dd MMM yyyy') : 'N/A'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={review.status}
                  size="small"
                  color={getStatusColor(review.status) as any}
                />
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      setSelectedReview(review);
                      setViewDialogOpen(true);
                    }}
                    title="View Full Review"
                  >
                    <Visibility />
                  </IconButton>
                  
                  {review.status === 'PENDING' && (
                    <>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleApprove(review.id)}
                        title="Approve"
                      >
                        <Check />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedReview(review);
                          setRejectDialogOpen(true);
                        }}
                        title="Reject"
                      >
                        <Close />
                      </IconButton>
                    </>
                  )}
                  
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, review)}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalElements}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </TableContainer>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Review Moderation
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => {
            setTabValue(newValue);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Pending Reviews" />
          <Tab label="Flagged Reviews" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reviews.length > 0 ? (
            renderReviewTable()
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
              No pending reviews
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reviews.length > 0 ? (
            renderReviewTable()
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
              No flagged reviews
            </Typography>
          )}
        </TabPanel>
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedReview) {
              setAdminResponse(selectedReview.adminResponse || '');
              setResponseDialogOpen(true);
            }
            handleMenuClose();
          }}
        >
          <Reply sx={{ mr: 1 }} fontSize="small" />
          Add Response
        </MenuItem>
        {selectedReview?.status !== 'FLAGGED' && (
          <MenuItem
            onClick={() => {
              if (selectedReview) {
                handleFlag(selectedReview.id);
              }
              handleMenuClose();
            }}
          >
            <Flag sx={{ mr: 1 }} fontSize="small" />
            Flag Review
          </MenuItem>
        )}
      </Menu>

      {/* View Review Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedReview && (
          <>
            <DialogTitle>
              Review Details
              <Chip
                label={selectedReview.status}
                size="small"
                color={getStatusColor(selectedReview.status) as any}
                sx={{ ml: 2 }}
              />
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Product</Typography>
                <Typography variant="body1">{selectedReview.productTitle}</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">User</Typography>
                <Typography variant="body1">
                  {selectedReview.userName}
                  {selectedReview.verified && (
                    <Chip label="Verified Purchase" size="small" color="success" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Rating</Typography>
                <StarRating rating={selectedReview.rating} />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                <Typography variant="body1">{selectedReview.title}</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Review</Typography>
                <Typography variant="body1">{selectedReview.comment}</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography variant="body1">
                  {selectedReview.createdAt ? format(new Date(selectedReview.createdAt), 'dd MMM yyyy HH:mm') : 'N/A'}
                </Typography>
              </Box>
              
              {selectedReview.adminResponse && (
                <Alert severity="info">
                  <Typography variant="subtitle2" gutterBottom>Admin Response</Typography>
                  <Typography variant="body2">{selectedReview.adminResponse}</Typography>
                  {selectedReview.adminResponseDate && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedReview.adminResponseDate ? format(new Date(selectedReview.adminResponseDate), 'dd MMM yyyy HH:mm') : ''}
                    </Typography>
                  )}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reject Review Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Review</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for Rejection"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            margin="normal"
            helperText="This reason will be sent to the user"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Response Dialog */}
      <Dialog open={responseDialogOpen} onClose={() => setResponseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Admin Response</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Response"
            value={adminResponse}
            onChange={(e) => setAdminResponse(e.target.value)}
            margin="normal"
            helperText="This response will be visible to all users"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddResponse} variant="contained">
            Add Response
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReviewModerationPage;